import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { IonContent, IonHeader, IonTitle, IonToolbar } from '@ionic/angular/standalone';

const SESSION_TIMEOUT_MAX = 300000;   // inactivity timer
const SESSION_TIMEOUT_IDLE = 60000;  // logout timer

@Component({
  selector: 'app-rs-session-tracker',
  templateUrl: './rs-session-tracker.page.html',
  styleUrls: ['./rs-session-tracker.page.scss'],
  standalone: true,
  imports: [IonContent, IonHeader, IonTitle, IonToolbar, CommonModule, FormsModule]
})
export class RsSessionTrackerPage implements OnInit {
  modalOpen = false;
  remainingSeconds = 0;

  private sessionTimer?: ReturnType<typeof setTimeout>;
  private idleTimer?: ReturnType<typeof setTimeout>;
  private countdownInterval?: ReturnType<typeof setInterval>;

  private readonly activityEvents = [
    'mousemove',
    'mousedown',
    'touchstart',
    'touchmove',
    'click',
    'keydown',
    'scroll',
  ] as const;

  private boundResetTimer = () => this.resetTimer();

  ngOnInit(): void {
    this.attachListeners();
    this.resetTimer();
  }

  ngOnDestroy(): void {
    this.detachListeners();
    this.clearAllTimers();
    document.body.classList.remove('app-scroll-locked');
  }

  private attachListeners(): void {
    this.activityEvents.forEach(event =>
      document.addEventListener(event, this.boundResetTimer, true)
    );
  }

  private detachListeners(): void {
    this.activityEvents.forEach(event =>
      document.removeEventListener(event, this.boundResetTimer, true)
    );
  }

  private resetTimer(): void {
    if (this.modalOpen) {
      return;
    }

    clearTimeout(this.sessionTimer);
    clearTimeout(this.idleTimer);

    this.sessionTimer = setTimeout(() => this.showIdleWarning(), SESSION_TIMEOUT_MAX);
  }

  private showIdleWarning(): void {
    this.modalOpen = true;
    document.body.classList.add('app-scroll-locked');
    this.startCountdown(SESSION_TIMEOUT_IDLE);
    this.idleTimer = setTimeout(() => {
      this.logout();
    }, SESSION_TIMEOUT_IDLE);
  }

  private startCountdown(durationMs: number): void {
    this.remainingSeconds = Math.floor(durationMs / 1000);

    this.countdownInterval = setInterval(() => {
      this.remainingSeconds--;
      if (this.remainingSeconds <= 0) {
        clearInterval(this.countdownInterval);
      }
    }, 1000);
  }

  /** "Stay logged in" button */
  staySignedIn(): void {
    this.modalOpen = false;
    document.body.classList.remove('app-scroll-locked');
    clearInterval(this.countdownInterval);
    clearTimeout(this.idleTimer);
    this.resetTimer();
  }

  /** "Logout" button or auto-logout when idle timer expires */
  logout(): void {
    this.modalOpen = false;
    document.body.classList.remove('app-scroll-locked');
    this.clearAllTimers();
    this.detachListeners();
    sessionStorage.clear();
    // Hard redirect bypasses Angular canDeactivate guards (e.g. unsaved-changes
    // dialogs on form screens) so the logout is never blocked.
    window.location.href = '/login';
  }

  private clearAllTimers(): void {
    clearTimeout(this.sessionTimer);
    clearTimeout(this.idleTimer);
    clearInterval(this.countdownInterval);
  }

}
